/**
 * LineageAudit — admin diagnostic page that buckets saved quotations
 * by the immutable `_lineage` stamp (per QUOTATION_GLOSSARY.md §3.8).
 *
 * Answers: "which engine / renderer produced which saved quote?"
 *
 * Source: GET /api/v1/admin/lineage  (tenant-scoped for non-superadmins).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { History, RefreshCw, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import api from '../../utils/api';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const LineageAudit = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const fetchLineage = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/lineage');
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load lineage audit');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLineage();
  }, [fetchLineage]);

  const total = data?.total_quotations ?? 0;
  const stamped = data?.stamped ?? 0;
  const unstamped = data?.unstamped ?? 0;
  const coverage = total ? Math.round((stamped / total) * 100) : 0;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="lineage-audit-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <History className="h-6 w-6" />
            Lineage Audit
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every saved quotation is stamped with an immutable <code>_lineage</code> record.
            Use this table to see which engine and renderer produced each slice of your saved data.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchLineage}
          disabled={loading}
          data-testid="lineage-refresh-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card data-testid="lineage-card-total">
          <CardHeader className="pb-2"><CardDescription>Total saved</CardDescription></CardHeader>
          <CardContent><div className="text-3xl font-semibold">{total}</div></CardContent>
        </Card>
        <Card data-testid="lineage-card-stamped">
          <CardHeader className="pb-2"><CardDescription>Stamped</CardDescription></CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold flex items-center gap-2">
              {stamped}
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            </div>
          </CardContent>
        </Card>
        <Card data-testid="lineage-card-unstamped">
          <CardHeader className="pb-2"><CardDescription>Unstamped (legacy)</CardDescription></CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold flex items-center gap-2">
              {unstamped}
              {unstamped > 0 && <AlertCircle className="h-5 w-5 text-amber-600" />}
            </div>
          </CardContent>
        </Card>
        <Card data-testid="lineage-card-coverage">
          <CardHeader className="pb-2"><CardDescription>Stamp coverage</CardDescription></CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold">{coverage}%</div>
          </CardContent>
        </Card>
      </div>

      {/* Buckets table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Buckets by engine × renderer × source</CardTitle>
          <CardDescription>
            Most active producers shown first. The day a quote looks wrong, check which bucket it sits in.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center text-sm text-muted-foreground" data-testid="lineage-loading">
              Loading…
            </div>
          ) : !data?.buckets?.length ? (
            <div className="py-8 text-center text-sm text-muted-foreground" data-testid="lineage-empty">
              No stamped quotations yet. Save a quote to populate this table.
            </div>
          ) : (
            <Table data-testid="lineage-buckets-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Created from</TableHead>
                  <TableHead>Pricing engine</TableHead>
                  <TableHead>Renderer</TableHead>
                  <TableHead>Schema</TableHead>
                  <TableHead className="text-right">Count</TableHead>
                  <TableHead>First seen</TableHead>
                  <TableHead>Last seen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.buckets.map((b, idx) => (
                  <TableRow key={`${b.created_from}-${b.pricing_engine_version}-${b.renderer_version}-${b.schema_version}`} data-testid={`lineage-bucket-row-${idx}`}>
                    <TableCell><Badge variant="outline">{b.created_from}</Badge></TableCell>
                    <TableCell>{b.pricing_engine_version}</TableCell>
                    <TableCell>{b.renderer_version}</TableCell>
                    <TableCell>v{b.schema_version}</TableCell>
                    <TableCell className="text-right font-semibold">{b.count}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{formatDate(b.first_seen)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{formatDate(b.last_seen)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LineageAudit;
